/**
 * SupplierPhotoZipTrigger
 *
 * Renders a "download supplier photos as ZIP" button on the catalog/inventory list
 * when exactly one supplier is in scope and the user can view media units.
 * Uses useSupplierPhotoZip() internally.
 *
 * Permission is resolved in the parent (apps/web/AGENTS.md) and passed as a boolean.
 *
 * Covers REQ-9.4 / Scenario: Trigger starts export for the scoped supplier.
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { useSupplierPhotoZip } from '../hooks/use-supplier-photo-zip';

interface SupplierPhotoZipTriggerProps {
  /** The single supplier currently in scope, or null when zero/multiple suppliers. */
  supplierId: number | null;
  /** Whether the current user has the media_unit.view permission. */
  canViewPhotos: boolean;
}

export function SupplierPhotoZipTrigger({
  supplierId,
  canViewPhotos,
}: SupplierPhotoZipTriggerProps) {
  const { t } = useTranslation('inventory');
  const { start } = useSupplierPhotoZip();

  if (!canViewPhotos || supplierId == null) return null;

  return (
    <Button
      data-testid="supplier-photo-zip-trigger"
      size="sm"
      variant="outline"
      onClick={() => void start({ supplierId })}
    >
      {t('supplierPhotoZip.trigger.label')}
    </Button>
  );
}
